"use client";

/**
 * Route-level error boundary.
 *
 * Next.js catches any thrown error inside a route segment (server or client)
 * and renders THIS component instead of crashing the page. Without it the
 * user sees a generic ugly "Application error" placeholder. With it they
 * see something branded, plus a Try again button and a WhatsApp fallback.
 *
 * The `digest` is Next.js's hashed error id — useful for cross-referencing
 * with Vercel's runtime logs ("error 138584060" style) when a guest reports
 * a problem.
 */

import { useEffect } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function RouteError({ error, reset }: Props) {
  useEffect(() => {
    // Log to the browser console so debug visitors / devtools see something
    // useful. Vercel runtime logs already capture the server side.
    // eslint-disable-next-line no-console
    console.error("[RouteError]", error);
  }, [error]);

  return (
    <>
      <Header />
      <main id="main">
        <section
          className="min-h-[calc(100vh-126px)] flex items-center justify-center text-center px-6 py-16"
          style={{
            background:
              "radial-gradient(at 20% 20%,#fee2e2,transparent 50%),radial-gradient(at 80% 0%,#fef3c7,transparent 50%),radial-gradient(at 0% 50%,#dbeafe,transparent 50%),#fff",
          }}
        >
          <div className="max-w-[560px]">
            <div
              className="h-display font-semibold text-[clamp(96px,18vw,180px)] leading-[0.85] mb-4"
              style={{
                background: "linear-gradient(135deg,#0a1d36,#4a76a3)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Oops
            </div>
            <h1 className="h-display text-[clamp(28px,4vw,40px)] font-semibold mb-3.5">
              Something hit a wave.
            </h1>
            <p className="text-base text-ss-muted leading-relaxed mb-7">
              We hit an unexpected error loading this page. The issue has been
              logged — try again, or message us on WhatsApp and we&apos;ll sort
              it out in under a minute.
            </p>
            <div className="flex gap-2.5 justify-center flex-wrap">
              <button
                type="button"
                onClick={reset}
                className="bg-ss-blue text-white px-6 py-3 rounded-full font-semibold cursor-pointer hover:bg-ss-blue-dark transition-colors"
              >
                Try again
              </button>
              <Link
                href="/"
                className="bg-white border border-ss-line px-6 py-3 rounded-full font-semibold hover:bg-ss-bg-soft"
              >
                Back to home
              </Link>
              <a
                href="https://wa.me/201044414367"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25d366] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#1ebe5a]"
              >
                WhatsApp us
              </a>
            </div>
            {error.digest && (
              <p className="text-[11px] text-ss-muted mt-6 font-mono">
                Reference: {error.digest}
              </p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
